/**
 * Smith Protective — ICPs Catalogue (Iter160).
 *
 * Vertical-by-vertical Ideal Customer Profile playbook — minimum property
 * size, headcount, likely guards, decision-makers and buying signals.
 * Serves as the sales BD reference card and drives the "on-ICP" chip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Target, Users, DollarSign, Zap, AlertTriangle, MapPin,
  Building2, Loader2, RefreshCw, Warehouse, Car, Home, Factory,
  HardHat, HeartPulse, ArrowRight, Server, Sliders, Radar, Sparkles,
  ChevronDown, ChevronUp,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";
const usd = (n) => (n ? `$${Math.round(n / 1000)}k` : "—");

const ICP_ICON = {
  warehouses: Warehouse,
  auto_auctions: Car,
  multifamily: Home,
  manufacturing: Factory,
  construction_sites: HardHat,
  healthcare: HeartPulse,
  data_centers: Server,
};

export default function SmithICPs() {
  const [icps, setIcps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diag, setDiag] = useState(null);           // Iter197 — enrichment diagnostics
  const [diagOpen, setDiagOpen] = useState(() => { // Iter233 · collapsed by default (space saver)
    try { return localStorage.getItem("smith.icps.diagOpen") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("smith.icps.diagOpen", diagOpen ? "1" : "0"); }
    catch { /* quota */ }
  }, [diagOpen]);
  const [searching, setSearching] = useState(null); // Iter201 — 0-match quick actions

  const triggerNewSearch = async (icp) => {
    setSearching(icp.id);
    try {
      const r = await axios.post(`${API}/smith/prospects/search-trigger`,
        { segment: icp.segment_id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success(r.data?.message || `Busca disparada para ${icp.name}`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao disparar busca — verifique credenciais de scraping");
    } finally {
      setSearching(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, diagRes] = await Promise.all([
        axios.get(`${API}/smith/icps`),
        axios.get(`${API}/smith/enrich/diagnostics`).catch(() => ({ data: null })),
      ]);
      setIcps(data.icps || []);
      setDiag(diagRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar ICPs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Iter197 — re-fetch when user returns to the tab (stale data guard).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-icps-header"
          title="Smith · ICP Playbook"
          subtitle={`${icps.length || 7} verticals · minimum bar, likely guards, decision-makers, buying signals`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
        >
          <Link to="/smith" data-testid="smith-dashboard-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Dashboard
          </Link>
          <Link to="/smith/prospects" data-testid="smith-prospects-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Prospects
          </Link>
          <button type="button" onClick={load} disabled={loading}
            data-testid="icps-refresh"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-4 pb-24 lg:pb-6"
              data-testid="smith-icps-page">
          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
            </div>
          ) : (
            <>
              <div className="rounded-2xl border p-4"
                   style={{ background: "rgba(30,58,138,0.15)", borderColor: "rgba(220,38,38,0.35)" }}>
                <p className="text-[13px] text-slate-200">
                  <span className="font-extrabold text-white">Como usar:</span>{" "}
                  Cada card é um perfil de cliente ideal (ICP) por vertical. O BD manager usa esse playbook para qualificar rapidamente uma lista de prospecção — se a conta bate o mínimo de metragem + funcionários, ela sobe para &quot;on-ICP&quot; e ganha um selo no Kanban.
                </p>
              </div>

              {/* Iter197 — Enrichment health card. Explains why Apollo/Hunter/Sunbiz
                  show empty circles in the enrich-history badges.
                  Iter233 — collapsed by default to free vertical real estate;
                  operator opens it only when investigating scraper health. */}
              {diag && diag.sources && (
                <div className="rounded-2xl border overflow-hidden"
                     style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                     data-testid="enrich-diagnostics-card">
                  <button type="button"
                          onClick={() => setDiagOpen((v) => !v)}
                          data-testid="enrich-diagnostics-toggle"
                          aria-expanded={diagOpen}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.03]">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-extrabold uppercase tracking-widest text-[#DC2626]">
                        Status dos apps de enrichment
                      </span>
                      <span className="text-[10.5px] text-slate-500">(últimas {diag.sample_size} execuções)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!diagOpen && (
                        <span className="text-[10.5px] text-slate-400">
                          {diag.sources.filter((s) => s.configured).length} configurados · {diag.sources.filter((s) => !s.configured).length} sem key
                        </span>
                      )}
                      {diagOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>
                  {diagOpen && (
                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {diag.sources.map((s) => {
                          const okCount = s.status_counts?.ok || 0;
                          const total = Object.values(s.status_counts || {}).reduce((a, b) => a + b, 0);
                          const healthy = s.configured && (okCount > 0 || s.last_status === "ok" || s.last_status === "already_known" || s.last_status === "no_new_fields");
                          return (
                            <div key={s.id}
                                 data-testid={`enrich-diag-${s.id}`}
                                 className="rounded-lg border p-3 space-y-2"
                                 style={{ borderColor: healthy ? "rgba(16,185,129,0.35)" : "rgba(245,158,11,0.4)",
                                           background: healthy ? "rgba(16,185,129,0.05)" : "rgba(245,158,11,0.05)" }}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${healthy ? "bg-emerald-400" : "bg-amber-400"}`} />
                                <span className="text-[12.5px] font-extrabold text-white flex-1">{s.name}</span>
                                {!s.configured && (
                                  <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-red-500/25 text-red-300">
                                    sem key
                                  </span>
                                )}
                              </div>
                              <div className="text-[10.5px] text-slate-400 leading-snug line-clamp-3">{s.purpose}</div>
                              <div className="text-[10.5px] text-slate-500">
                                {Object.entries(s.status_counts || {}).map(([k, v]) => (
                                  <span key={k} className="inline-block mr-2 tabular-nums">
                                    <span className={k === "ok" ? "text-emerald-400" : "text-slate-400"}>{k}</span>: <b>{v}</b>
                                  </span>
                                ))}
                                {total === 0 && <span className="italic text-slate-500">sem execuções recentes</span>}
                              </div>
                              {s.hint && (
                                <div className="text-[10.5px] text-amber-200 leading-snug">
                                  💡 {s.hint}
                                </div>
                              )}
                              {s.action_url && (
                                <a href={s.action_url} target="_blank" rel="noreferrer"
                                   data-testid={`enrich-diag-action-${s.id}`}
                                   className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#DC2626] hover:underline">
                                  Resolver agora ↗
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
                {icps.map((icp) => {
                  const Icon = ICP_ICON[icp.id] || Building2;
                  return (
                    <article key={icp.id}
                             data-testid={`icp-card-${icp.id}`}
                             className="rounded-xl border p-3 space-y-2"
                             style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
                      <header className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                               style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-[13px] font-extrabold text-white leading-tight truncate">{icp.name}</h3>
                            <p className="text-[10.5px] text-slate-400 mt-0.5 line-clamp-2">{icp.description}</p>
                          </div>
                        </div>
                        <Link
                          to={`/smith/prospects?segment=${icp.segment_id}`}
                          data-testid={`icp-open-prospects-${icp.id}`}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 whitespace-nowrap inline-flex items-center gap-1">
                          {icp.matching_prospects} <ArrowRight className="w-2.5 h-2.5" />
                        </Link>
                      </header>

                      {/* Iter201 — Quick Actions on 0-match cards */}
                      {icp.matching_prospects === 0 && (
                        <div className="rounded-lg border border-amber-500/40 p-2.5 space-y-2"
                             style={{ background: "rgba(245,158,11,0.06)" }}
                             data-testid={`icp-zero-match-actions-${icp.id}`}>
                          <div className="text-[11px] font-bold text-amber-200 flex items-center gap-1.5">
                            <AlertTriangle className="w-3 h-3" />
                            Nenhum prospect nessa vertical ainda
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Link
                              to={`/smith/prospects?segment=${icp.segment_id}&relaxFilters=1`}
                              data-testid={`icp-adjust-filters-${icp.id}`}
                              title="Abre a listagem com filtros relaxados (segmento aplicado, min_sqft/min_empl removidos)"
                              className="h-7 px-2.5 rounded text-[10.5px] font-bold text-slate-200 border border-white/15 hover:border-white/30 inline-flex items-center gap-1">
                              <Sliders className="w-2.5 h-2.5" /> Ajustar Filtros
                            </Link>
                            <button type="button"
                                    onClick={() => triggerNewSearch(icp)}
                                    disabled={searching === icp.id}
                                    data-testid={`icp-trigger-search-${icp.id}`}
                                    title={`Rodar scrape do Sunbiz/Places pro segmento ${icp.name}`}
                                    className="h-7 px-2.5 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
                                    style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                              {searching === icp.id
                                ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Buscando…</>
                                : <><Radar className="w-2.5 h-2.5" /> Disparar Nova Busca</>}
                            </button>
                            <Link
                              to={`/smith/outreach?segment=${icp.segment_id}`}
                              data-testid={`icp-ai-suggest-${icp.id}`}
                              title="Ir ao Outreach AI para gerar personas com IA quando bases retornam vazias"
                              className="h-7 px-2.5 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1"
                              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
                              <Sparkles className="w-2.5 h-2.5" /> AI Sugerir
                            </Link>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-4 gap-1.5">
                        <Metric label="Min sqft" v={icp.min_sqft_building?.toLocaleString()} />
                        <Metric label="Min empl." v={icp.min_employees?.toLocaleString()} />
                        <Metric label="Guards" v={`${icp.likely_guards[0]}–${icp.likely_guards[1]}`} />
                        <Metric label="ACV" v={`${usd(icp.annual_ticket[0])}–${usd(icp.annual_ticket[1])}`} />
                      </div>

                      <div className="flex items-center justify-between text-[10.5px]">
                        <span className="text-slate-500">{(icp.coverage || "").replace("_", " ")}</span>
                        <span className="text-emerald-400 font-bold">${icp.hourly_rate}/hr</span>
                      </div>

                      <div>
                        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
                          <Users className="w-2.5 h-2.5" /> Decision-makers
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {icp.decision_makers.slice(0, 6).map((d) => (
                            <span key={d} className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/10 text-slate-200">{d}</span>
                          ))}
                          {icp.decision_makers.length > 6 && (
                            <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">+{icp.decision_makers.length - 6}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5 text-amber-400" /> Buying signals
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {icp.buying_signals.slice(0, 4).map((s) => (
                            <span key={s} className="text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 capitalize">{s.replace("_", " ")}</span>
                          ))}
                          {icp.buying_signals.length > 4 && (
                            <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">+{icp.buying_signals.length - 4}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5 text-red-400" /> Pain points
                        </div>
                        <ul className="text-[10.5px] text-slate-300 space-y-0.5 list-disc pl-4">
                          {icp.pain_points.slice(0, 3).map((p) => <li key={p} className="line-clamp-1">{p}</li>)}
                          {icp.pain_points.length > 3 && (
                            <li className="text-slate-500 italic list-none">+{icp.pain_points.length - 3} mais</li>
                          )}
                        </ul>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Metric({ label, v }) {
  return (
    <div className="rounded border border-white/5 p-1.5 bg-white/[0.02]">
      <div className="text-[8.5px] uppercase tracking-widest text-slate-500 font-bold truncate">{label}</div>
      <div className="text-[11px] font-extrabold text-white tabular-nums truncate">{v || "—"}</div>
    </div>
  );
}
